/* [BD feat] Icon Curve — Web Component for icon-curve-button shape */
/**
 * bd-icon-curve — Wraps any element (button, input, card, badge) with the
 * icon-curve SVG shape. Scales the SVG to match the host's dimensions via
 * ResizeObserver.
 *
 * Attributes:
 *   data-fill   — SVG fill color  (default: #C7E3A7)
 *   data-stroke — SVG stroke color (default: transparent — pass a color when a border is needed)
 *   data-stroke-opacity — SVG stroke opacity 0–1 (default: 1)
 *
 * Usage:
 *   <bd-icon-curve data-fill="#00442A">
 *     <button class="btn">Click Me</button>
 *   </bd-icon-curve>
 */
(function () {
  'use strict';

  const ORIG_W = 300;
  const ORIG_H = 52;
  const LC = 24;  // left cap width  — ends exactly where the rounded cap ends
  const RC = 26;  // right cap width — ends exactly where the rounded cap ends
  const MAX_CAP_RATIO = 0.5;  // caps may never consume more than half the width

  // Resting y of the hand-drawn wave on each long edge — the level the outline
  // returns to between crests. Wave amplitude is measured against these.
  const TOP_BASE = 1.73255;
  const BOT_BASE = 49.089;
  const EDGE_BAND = 4;    // a point is "on a long edge" within this distance of its base
                          // (must clear the deepest crest, y 52.5541, or it alone stays undamped)
  const MID_REF = 0.35;   // horizontal squeeze below which the wave starts flattening
  const DAMP_MIN = 0.3;   // keep some wave even on the narrowest hosts

  // ── Path scaling ─────────────────────────────────────────────────────────
  //
  // X: 3-zone mapping keeps the rounded pill ends intact at any width.
  //   left cap  (x ≤ LC)          → held at original size
  //   right cap (x ≥ ORIG_W - RC) → held at original size, anchored to the right edge
  //   middle                       → proportionally squeezed / stretched
  //
  // LC/RC must hug the caps and nothing more. The top and bottom edges carry a
  // hand-drawn wave between x≈24 and x≈276; any of it left inside a "held" zone
  // gets no compression while its neighbours get plenty, and the tangent break at
  // the zone boundary shows up as a sharp notch in the outline.
  //
  // On very narrow hosts the held caps would leave the middle almost no room,
  // collapsing the wave into a spike, so the caps shrink together (via k) once
  // they would exceed MAX_CAP_RATIO of the width. k is 1 at normal button sizes.
  //
  // Y: uniform proportional scale so the shape fills any height correctly, plus
  // amplitude damping on the two long edges — see dampY.

  function capScale(W) {
    return Math.min(1, (W * MAX_CAP_RATIO) / (LC + RC));
  }

  function remapX(x, W) {
    const k = capScale(W);
    const lc = LC * k;
    const rc = RC * k;
    if (x <= LC) return x * k;
    if (x >= ORIG_W - RC) return W - (ORIG_W - x) * k;
    return lc + (x - LC) * ((W - lc - rc) / (ORIG_W - LC - RC));
  }

  // How much the middle zone is squeezed horizontally, 1 = original proportions.
  function midRatio(W) {
    const k = capScale(W);
    return Math.min(1, (W - LC * k - RC * k) / (ORIG_W - LC - RC));
  }

  // The wave keeps its full height no matter how far the middle zone is squeezed,
  // so on a narrow host a crest that spanned 70px of the source is packed into a
  // few pixels and reads as a sharp spike. Flatten the wave in step with the
  // squeeze so its proportions survive. Only points sitting on a long edge move;
  // the cap coordinates are left alone.
  function dampY(y, W) {
    const base = y < ORIG_H / 2 ? TOP_BASE : BOT_BASE;
    if (Math.abs(y - base) > EDGE_BAND) return y;
    const r = Math.max(DAMP_MIN, Math.min(1, midRatio(W) / MID_REF));
    return base + (y - base) * r;
  }

  function remapY(y, H) {
    return y * (H / ORIG_H);
  }

  function p(x, y, W, H) {
    return `${+remapX(x, W).toFixed(3)} ${+remapY(dampY(y, W), H).toFixed(4)}`;
  }

  function buildFillPath(W, H) {
    const Wr = +W.toFixed(3);
    return [
      `M0 ${+remapY(25.6974, H).toFixed(4)}`,
      `C0 ${+remapY(12.4242, H).toFixed(4)} ${p(10.6819, 1.62766, W, H)} ${p(23.9549, 1.55166, W, H)}`,
      `C${p(41.7127, 1.44999, W, H)} ${p(63.9585, 1.40151, W, H)} ${p(70.7143, 1.73255, W, H)}`,
      `C${p(82.5, 2.31007, W, H)} ${p(109.286, 0, W, H)} ${p(115.714, 0, W, H)}`,
      `C${p(122.143, 0, W, H)} ${p(130.714, 1.73255, W, H)} ${p(142.5, 1.73255, W, H)}`,
      `H${+remapX(275.958, W).toFixed(3)}`,
      `C${p(289.255, 1.73255, W, H)} ${Wr} ${+remapY(12.512, H).toFixed(4)} ${Wr} ${+remapY(25.8092, H).toFixed(4)}`,
      `C${Wr} ${+remapY(39.2454, H).toFixed(4)} ${p(289.015, 50.082, W, H)} ${p(275.58, 49.8897, W, H)}`,
      `C${p(251.854, 49.5502, W, H)} ${p(218.631, 49.089, W, H)} ${p(213.214, 49.089, W, H)}`,
      `C${p(204.643, 49.089, W, H)} ${p(184.286, 50.2441, W, H)} ${p(178.929, 51.3991, W, H)}`,
      `C${p(173.571, 52.5541, W, H)} ${p(143.571, 51.9766, W, H)} ${p(132.857, 50.244, W, H)}`,
      `C${p(125.208, 49.0072, W, H)} ${p(60.77, 49.5363, W, H)} ${p(24.5241, 49.9403, W, H)}`,
      `C${p(11.0249, 50.0908, W, H)} 0 ${+remapY(39.1975, H).toFixed(4)} 0 ${+remapY(25.6974, H).toFixed(4)}`,
      `Z`,
    ].join(' ');
  }

  // ── Dimension reading ─────────────────────────────────────────────────────
  //
  // Width  → read from the wrapper (it has explicit width set via CSS/style).
  // Height → read from the child element directly.
  //
  // Why not wrapper.offsetHeight?
  //   The wrapper has no explicit height — it sizes to its child's content box.
  //   In some browser/theme contexts (especially with padding-driven heights and
  //   box-sizing: content-box), wrapper.offsetHeight returns the content height
  //   only, not the full rendered height including padding.
  //   child.offsetHeight is always the full border-box height of the child.

  function getDimensions(host) {
    const child = Array.from(host.children)
      .find(c => c.tagName.toLowerCase() !== 'svg' && !c.hasAttribute('data-bd-svg'));
    const W = host.offsetWidth || ORIG_W;
    const H = child ? child.offsetHeight : (host.offsetHeight || ORIG_H);
    return { W, H };
  }

  // ── Web Component ─────────────────────────────────────────────────────────

  class BdIconCurve extends HTMLElement {

    static get observedAttributes() {
      return ['data-fill', 'data-stroke', 'data-stroke-opacity'];
    }

    connectedCallback() {
      // Double rAF: first frame lets CSS apply, second lets layout complete.
      // This ensures offsetWidth / offsetHeight are non-zero on first render.
      requestAnimationFrame(() => requestAnimationFrame(() => {
        this._render();

        this._ro = new ResizeObserver(() => this._render());

        // Observe wrapper for width changes (wrapper has explicit width).
        this._ro.observe(this);

        // Observe child for height changes (child owns the true height).
        const child = Array.from(this.children)
          .find(c => c.tagName.toLowerCase() !== 'svg');
        if (child) this._ro.observe(child);
      }));
    }

    disconnectedCallback() {
      this._ro?.disconnect();
      this._ro = null;
    }

    attributeChangedCallback() {
      this._render();
    }

    _render() {
      const { W, H } = getDimensions(this);
      if (!W || !H) return;

      const fillColor = this.dataset.fill || '#C7E3A7';
      const strokeColor = this.dataset.stroke || 'transparent';
      const strokeOpacity = this.dataset.strokeOpacity || '1';

      const svgNS = 'http://www.w3.org/2000/svg';
      let svg = this.querySelector(':scope > svg[data-bd-svg]');

      if (!svg) {
        svg = document.createElementNS(svgNS, 'svg');
        svg.setAttribute('fill', 'none');
        svg.setAttribute('xmlns', svgNS);
        svg.setAttribute('aria-hidden', 'true');
        svg.setAttribute('data-bd-svg', '');

        const fillEl = document.createElementNS(svgNS, 'path');
        fillEl.dataset.role = 'fill';

        const strokeEl = document.createElementNS(svgNS, 'path');
        strokeEl.dataset.role = 'stroke';
        strokeEl.setAttribute('fill', 'none');

        svg.appendChild(fillEl);
        svg.appendChild(strokeEl);
        this.insertBefore(svg, this.firstChild);
      }

      svg.setAttribute('viewBox', `0 0 ${W} ${H}`);

      const fillEl = svg.querySelector('[data-role="fill"]');
      const strokeEl = svg.querySelector('[data-role="stroke"]');

      // Stroke reuses the fill geometry so the border always hugs the shape exactly.
      const d = buildFillPath(W, H);

      fillEl.setAttribute('d', d);
      fillEl.setAttribute('fill', fillColor);

      strokeEl.setAttribute('d', d);
      strokeEl.setAttribute('stroke', strokeColor);

      const isStrokeVisible = strokeColor && strokeColor !== 'transparent' && strokeColor !== 'rgba(0,0,0,0)';
      strokeEl.setAttribute('stroke-opacity', isStrokeVisible ? strokeOpacity : '0');
      strokeEl.setAttribute('stroke-width', isStrokeVisible ? '1' : '0');
    }
  }

  if (!customElements.get('bd-icon-curve')) {
    customElements.define('bd-icon-curve', BdIconCurve);
  }
})();
/* [BD feat] Icon Curve — end */